import React from 'react';
import { Users, Calendar, AlertTriangle, Briefcase, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const StatsCards = ({ stats }) => {
  // Each card scrolls to its matching widget on the dashboard (a "shortcut"),
  // instead of navigating away to a separate page.
  const scrollToSection = (sectionId) => {
    const el = document.querySelector(`[data-testid="section-${sectionId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('blink-highlight');
    setTimeout(() => el.classList.remove('blink-highlight'), 2400);
  };

  const cards = [
    {
      title: 'Active Staff',
      value: stats?.active_employees || 0,
      sub: `Total: ${stats?.total_employees || 0}`,
      icon: Users,
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      valueColor: 'text-emerald-700',
      target: 'employment-status',
    },
    {
      title: 'Currently On Leave',
      value: stats?.on_leave_today || 0,
      sub: 'Staff away now',
      icon: Calendar,
      bg: 'bg-sky-50',
      border: 'border-sky-200',
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      valueColor: 'text-sky-700',
      target: 'current-leaves',
    },
    {
      title: 'Upcoming Leaves',
      value: stats?.upcoming_leaves || 0,
      sub: 'Next 30 days',
      icon: TrendingUp,
      bg: 'bg-violet-50',
      border: 'border-violet-200',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      valueColor: 'text-violet-700',
      target: 'upcoming-leaves',
    },
    {
      title: 'Expiring Documents',
      value: stats?.expiring_documents || 0,
      sub: 'Next 90 days',
      icon: AlertTriangle,
      bg: 'bg-rose-50',
      border: 'border-rose-200',
      iconBg: 'bg-rose-100',
      iconColor: 'text-rose-600',
      valueColor: 'text-rose-700',
      target: 'expiring-docs',
    },
    {
      title: 'Departments',
      value: Object.keys(stats?.departments || {}).length,
      sub: 'Active departments',
      icon: Briefcase,
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      valueColor: 'text-amber-700',
      target: 'department',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="stats-cards">
      {cards.map(c => (
        <Card
          key={c.title}
          onClick={() => scrollToSection(c.target)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scrollToSection(c.target); } }}
          className={`${c.bg} border ${c.border} shadow-none cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-violet-400`}
          title={`Jump to: ${c.title}`}
          data-testid={`stat-card-${c.title.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-700">{c.title}</CardTitle>
            <div className={`w-8 h-8 rounded-lg ${c.iconBg} flex items-center justify-center`}>
              <c.icon className={`h-4 w-4 ${c.iconColor}`} />
            </div>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${c.valueColor}`} data-testid={`stat-${c.title.toLowerCase().replace(/\s+/g, '-')}`}>
              {c.value}
            </div>
            <p className="text-[11px] text-gray-500 mt-1">{c.sub}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default StatsCards;
